// ./src/components/generic/ScheduleDaysInput/ScheduleDaysInput.tsx
import React, { useCallback, useMemo } from "react";
import { Text, Input } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

import { useAppEnvironment } from "@/contexts/AppEnvironmentContext";

import { WeekDaySelector } from "../WeekDaySelector";
import { MonthDaySelector } from "../MonthDaySelector";
import { YearDaySelector } from "../YearDaySelector";

// ============================================
// Types
// ============================================

export type ScheduleType = "weekly" | "monthly" | "yearly" | "one-time";

export interface ScheduleDaysInputProps {
  /** Schedule type determines which selector to show */
  scheduleType: ScheduleType | null;

  /** Selected days/dates as comma-separated string (matches API format) */
  value: string;

  /** Callback when selection changes */
  onChange: (value: string) => void;

  /** Optional label */
  label?: string;

  /** Optional description (if not provided, auto-generates based on scheduleType) */
  description?: string;

  /** Error message */
  error?: string;

  /** Whether the field is required */
  required?: boolean;

  /** Whether the selector is disabled */
  disabled?: boolean;

  /** Size variant */
  size?: "xs" | "sm" | "md" | "lg";

  /** Variant for weekday display */
  weekDayVariant?: "short" | "long";
}

// ============================================
// Helper Functions
// ============================================

/**
 * Parse comma-separated string to array
 */
const parseValue = (value: string): string[] => {
  if (!value || value.trim() === "") {
    return [];
  }
  return value.split(",").map((v) => v.trim()).filter(Boolean);
};

/**
 * Format array to comma-separated string
 */
const formatValue = (values: string[]): string => {
  return values.join(",");
};

/**
 * Parse weekday string values to numbers
 */
const parseWeekDays = (value: string): number[] => {
  return parseValue(value)
    .map((v) => parseInt(v, 10))
    .filter((n) => !isNaN(n) && n >= 1 && n <= 7);
};

/**
 * Format weekday numbers to string
 */
const formatWeekDays = (days: number[]): string => {
  return days.map(String).join(",");
};

/**
 * Convert YYYY-MM-DD to Mantine DatePickerInput format (YYYY-MM-DD)
 * DatePickerInput in Mantine 8.x uses string values
 */
const parseOneTimeDate = (value: string): string | null => {
  if (!value || value.trim() === "") {
    return null;
  }
  // Validate the date format
  const parsed = dayjs(value, "YYYY-MM-DD", true);
  return parsed.isValid() ? value : null;
};

/**
 * Format Mantine DatePickerInput string value to YYYY-MM-DD
 */
const formatOneTimeDate = (dateStr: string | null): string => {
  if (!dateStr) {
    return "";
  }
  // DatePickerInput returns date in the format we need
  const parsed = dayjs(dateStr);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";
};

// ============================================
// Component
// ============================================

const ScheduleDaysInputComponent: React.FC<ScheduleDaysInputProps> = ({
  scheduleType,
  value,
  onChange,
  label,
  description,
  error,
  required = false,
  disabled = false,
  size = "md",
  weekDayVariant = "short",
}) => {
  const { t } = useTranslation(["common"]);
  const { isMobile } = useAppEnvironment();

  // Get auto-generated description based on schedule type
  const autoDescription = useMemo(() => {
    if (description) return description;

    switch (scheduleType) {
      case "weekly":
        return t("common:selectors.scheduleDaysInput.weeklyDescription");
      case "monthly":
        return t("common:selectors.scheduleDaysInput.monthlyDescription");
      case "yearly":
        return t("common:selectors.scheduleDaysInput.yearlyDescription");
      case "one-time":
        return t("common:selectors.scheduleDaysInput.oneTimeDescription");
      default:
        return undefined;
    }
  }, [scheduleType, description, t]);

  // Handle weekly change
  const handleWeeklyChange = useCallback(
    (days: number[]) => {
      onChange(formatWeekDays(days));
    },
    [onChange]
  );

  // Handle monthly change
  const handleMonthlyChange = useCallback(
    (days: string[]) => {
      onChange(formatValue(days));
    },
    [onChange]
  );

  // Handle yearly change
  const handleYearlyChange = useCallback(
    (dates: string[]) => {
      onChange(formatValue(dates));
    },
    [onChange]
  );

  // Handle one-time date change (Mantine 8.x DatePickerInput uses string | null)
  const handleOneTimeChange = useCallback(
    (dateStr: string | null) => {
      onChange(formatOneTimeDate(dateStr));
    },
    [onChange]
  );

  // Get minimum date for one-time (tomorrow)
  const minDate = useMemo(() => {
    return dayjs().add(1, "day").format("YYYY-MM-DD");
  }, []);

  // Render nothing if no schedule type selected
  if (!scheduleType) {
    return (
      <Input.Wrapper
        label={label}
        description={t("common:selectors.scheduleDaysInput.selectTypeFirst")}
        error={error}
        required={required}
        size={size}
      >
        <Text size="sm" c="dimmed" mt="xs">
          {t("common:selectors.scheduleDaysInput.selectTypeFirst")}
        </Text>
      </Input.Wrapper>
    );
  }

  // Date dropdown type: modal on mobile for better UX, popover on desktop
  const dateDropdownType = isMobile ? "modal" : "popover";

  // Render appropriate selector based on schedule type
  switch (scheduleType) {
    case "weekly":
      return (
        <WeekDaySelector
          value={parseWeekDays(value)}
          onChange={handleWeeklyChange}
          label={label}
          description={autoDescription}
          error={error}
          required={required}
          disabled={disabled}
          size={size}
          variant={weekDayVariant}
          multiple
        />
      );

    case "monthly":
      return (
        <MonthDaySelector
          value={parseValue(value)}
          onChange={handleMonthlyChange}
          label={label}
          description={autoDescription}
          error={error}
          required={required}
          disabled={disabled}
          size={size}
          multiple
          showLastDay
        />
      );

    case "yearly":
      return (
        <YearDaySelector
          value={parseValue(value)}
          onChange={handleYearlyChange}
          label={label}
          description={autoDescription}
          error={error}
          required={required}
          disabled={disabled}
          size={size}
          maxDates={12}
        />
      );

    case "one-time":
      return (
        <DatePickerInput
          label={label}
          description={autoDescription}
          placeholder={t("common:selectors.scheduleDaysInput.selectDate")}
          value={parseOneTimeDate(value)}
          onChange={handleOneTimeChange}
          error={error}
          required={required}
          disabled={disabled}
          size={size}
          minDate={minDate}
          valueFormat="MMM DD, YYYY"
          clearable
          dropdownType={dateDropdownType}
        />
      );

    default:
      return null;
  }
};

export const ScheduleDaysInput = React.memo(ScheduleDaysInputComponent);